import { BackgroundColors, SecondaryColors } from '@picsart/design-system/foundation/colors';
import { getPrice } from '@picsart/monetization/subscriptionPlansService';

import {
  ButtonSkinAbsoluteBlack,
  ButtonSkinPrimary,
  ButtonSkinSecondary,
  ButtonVariants,
} from '@picsart/design-system/components/Button';

import {
  DEFAULT_SCOPE_ID,
  DEFAULT_TEAM_SCOPE_ID,
  GOLD_LOGO,
  PLUS_LOGO,
  PRO_LOGO,
  STORE_URL,
} from '@picsart/monetization/constants';
import {
  SubscriptionPlan,
  SubscriptionPlanFrequencyType,
} from '@picsart/monetization/types/models/subscriptionPlanModel';
import { toFixedPrice } from '@picsart/monetization/currencyService';

import { PricingCardProps } from './PricingCard/types';

const pricingUrl = '/pricing#plans';
const editorUrl = '/create/editor';
enum FREQUENCY {
  YEARLY = 'yearly',
  MONTHLY = 'monthly',
}

const tierCards: PricingCardProps[] = [
  {
    cardId: 'free',
    cardName: 'Free',
    monthlyPrice: 0,
    currencySymbol: '$',
    cardHref: editorUrl,
    cardColor: BackgroundColors.tint3,
    cardTitle: 'Craft exceptional content',
    cardDescription:
      'Selection of free images, videos, and templates to create original content, without breaking the bank.',
    cardButtonLabel: 'Get started',
    cardButtonProps: {
      variant: 'Outline' as ButtonVariants,
      skin: ButtonSkinAbsoluteBlack,
    },
  },
  {
    cardId: 'plus',
    cardLogo: PLUS_LOGO,
    monthlyPrice: 0,
    currencySymbol: '$',
    cardHref: pricingUrl,
    cardColor: BackgroundColors.tint3,
    cardNote: 'Free for {{count}} days. Cancel anytime.',
    cardTitle: 'Elevate your designs',
    cardDescription: 'Access to premium content and templates to level up your designs.',
    cardButtonLabel: 'Learn about plus',
    cardButtonProps: {
      variant: 'Outline' as ButtonVariants,
      skin: ButtonSkinAbsoluteBlack,
    },
  },
  {
    cardId: 'pro',
    cardLogo: PRO_LOGO,
    monthlyPrice: 0,
    currencySymbol: '$',
    cardHref: pricingUrl,
    cardColor: SecondaryColors.base.default,
    cardNote: 'Free for {{count}} days. Cancel anytime.',
    cardTitle: 'Maximize creative efficiency',
    cardDescription:
      'Full suite of powerful AI tools, with centralized billing and admin controls to scale as your business grows.',
    cardButtonLabel: 'Learn about pro',
    cardButtonProps: {
      variant: 'Filled' as ButtonVariants,
      skin: ButtonSkinSecondary,
    },
  },
];

const goldCards: any[] = [
  {
    cardId: 'free',
    cardName: 'Free',
    monthlyPrice: 0,
    currencySymbol: '$',
    cardHref: editorUrl,
    cardColor: BackgroundColors.tint3,
    cardTitle: 'Craft exceptional content',
    cardDescription:
      'Selection of free images, videos, and templates to create original content, without breaking the bank.',
    cardButtonLabel: 'Get started',
    cardButtonProps: {
      variant: 'Outline',
      skin: ButtonSkinAbsoluteBlack,
    },
  },
  {
    cardId: 'gold',
    cardLogo: GOLD_LOGO,
    monthlyPrice: 0,
    currencySymbol: '$',
    cardHref: pricingUrl,
    cardColor: SecondaryColors.base.default,
    cardNote: 'Free for {{count}} days. Cancel anytime.',
    cardTitle: ['Elevate your designs'],
    description:
      'Access to the full suite of AI tools and premium content, stickers, fonts, and templates.',
    buttonLabel: 'Learn about {{tier}}',
    buttonProps: {
      variant: 'Filled',
      skin: ButtonSkinPrimary,
    },
  },
  {
    cardId: 'team',
    cardName: 'Team',
    monthlyPrice: 0,
    currencySymbol: '$',
    cardHref: pricingUrl,
    cardColor: SecondaryColors.base.default,
    cardNote: 'Free for {{count}} days. Cancel anytime.',
    cardTitle: 'Maximize creative efficiency',
    cardDescription: 'Centralized billing and admin controls to scale as your business grows.',
    cardButtonLabel: 'Learn about {{tier}}',
    cardButtonProps: {
      variant: 'Filled',
      skin: ButtonSkinSecondary,
    },
  },
];

export const getCmsCards = (
  subscriptionPlans: SubscriptionPlan[],
  frequency: SubscriptionPlanFrequencyType,
  cards: PricingCardProps[],
  isMobile?: boolean,
) => {
  return cards.map(item => {
    const planData = subscriptionPlans.find(
      plan => plan.tier_id === item?.tierId && plan.frequency === frequency,
    );

    const { currencySymbol, currency } = planData?.price || {};
    const cardNote = item?.cardNote?.replace('{{count}}', `${planData?.trial_period_days}`);
    const cardHref = item.cardId === 'free' && isMobile ? STORE_URL : item.cardHref;
    const planPrice = getPrice(planData, currency, true);
    const monthlyPrice =
      !planPrice || item.tierId.includes('custom')
        ? 'Custom'
        : toFixedPrice(planPrice / (frequency === FREQUENCY.YEARLY ? 12 : 1));

    return {
      ...item,
      currencySymbol,
      monthlyPrice,
      cardHref,
      cardNote,
    };
  });
};

export const getGoldCards = (
  subscriptionPlans: SubscriptionPlan[],
  frequency: SubscriptionPlanFrequencyType,
  isMobile?: boolean,
): PricingCardProps[] => {
  const goldYearlyPlan = subscriptionPlans.find(
    plan => plan.scope_id === DEFAULT_SCOPE_ID && plan.frequency === frequency,
  );
  const teamYearlyPlan = subscriptionPlans.find(
    plan => plan.scope_id === DEFAULT_TEAM_SCOPE_ID && plan.frequency === frequency,
  );

  if (!goldYearlyPlan || !teamYearlyPlan) {
    return [];
  }

  return goldCards.map(item => {
    const { currencySymbol } = goldYearlyPlan.price;
    const cardNote = item?.cardNote?.replace('{{count}}', `${goldYearlyPlan.trial_period_days}`);
    const cardButtonLabel = item?.cardButtonLabel?.replace('{{tier}}', `${goldYearlyPlan.tier_id}`);
    const cardHref = item.cardId === 'free' && isMobile ? STORE_URL : item.cardHref;
    const monthlyPrice =
      // eslint-disable-next-line no-nested-ternary
      item?.cardId === 'gold'
        ? toFixedPrice(goldYearlyPlan.price.value / (frequency === FREQUENCY.YEARLY ? 12 : 1))
        : item.cardId === 'team'
        ? toFixedPrice(
            (teamYearlyPlan.price.initial_value || 0) / (frequency === FREQUENCY.YEARLY ? 12 : 1),
          )
        : 0;

    return {
      ...item,
      currencySymbol,
      monthlyPrice,
      cardButtonLabel,
      cardHref,
      cardNote,
    };
  });
};

export const getTierCards = (
  subscriptionPlans: SubscriptionPlan[],
  frequency: SubscriptionPlanFrequencyType,
  isMobile?: boolean,
) => {
  const plusYearlyPlan = subscriptionPlans.find(
    plan => plan.tier_id === 'plus' && plan.frequency === frequency,
  );
  const proYearlyPlan = subscriptionPlans.find(
    plan => plan.tier_id === 'pro' && plan.frequency === frequency,
  );

  if (!plusYearlyPlan || !proYearlyPlan) {
    return [];
  }

  return tierCards.map(item => {
    const { currencySymbol } = plusYearlyPlan.price;
    const cardNote = item?.cardNote?.replace('{{count}}', `${plusYearlyPlan.trial_period_days}`);
    const cardHref = item.cardId === 'free' && isMobile ? STORE_URL : item.cardHref;
    const monthlyPrice =
      // eslint-disable-next-line no-nested-ternary
      item.cardId === 'pro'
        ? toFixedPrice(
            (proYearlyPlan.price.initial_value || 0) / (frequency === FREQUENCY.YEARLY ? 12 : 1),
          )
        : item.cardId === 'plus'
        ? toFixedPrice(plusYearlyPlan.price.value / (frequency === FREQUENCY.YEARLY ? 12 : 1))
        : 0;

    return {
      ...item,
      currencySymbol,
      monthlyPrice,
      cardHref,
      cardNote,
    };
  });
};

export default {
  getTierCards,
  getGoldCards,
};
